import {
  Gym, GymTypes, IEnabledFeatures, ILeaderboardInstance,
  IUserLeaderboardInstance,
  IUserWorkoutAggregate,
  IUserWorkoutSessionSummary,
  LeaderboardData,
  NextWorkout,
  TimeSpan,
} from 'fitbeat.models';
import { IExternalReference, UserWorkoutSession, WorkoutSettings } from '../../../entities';
import { DbGymRepository } from '../db';
import { IWorkoutRepository } from '../interfaces';

export class InMemoryWorkoutRepository implements IWorkoutRepository {
  private userWorkoutSessions = new Array<UserWorkoutSession>();

  public getAll(gym: Gym): Promise<UserWorkoutSession[]> {
    return new Promise((resolve) => {
      const filteredByGym = this.userWorkoutSessions
        .filter((userWorkoutSession: UserWorkoutSession) => userWorkoutSession.gymName === gym.name);
      resolve(filteredByGym);
    });
  }

  public async getAllWorkoutsForUsers(
    userProfileIds: string[],
    from: Date,
    to: Date,
    ignoreCheckIn: boolean = false,
  ): Promise<UserWorkoutSession[]> {
    return this.userWorkoutSessions.filter((session) => userProfileIds.indexOf(session.userProfileId) >= 0
        && !session.removedAt
        && session.endTime.getTime() >= from.getTime()
        && session.endTime.getTime() <= to.getTime());
  }

  public async getAllWorkoutsForSingleUser(userProfileId: string): Promise<UserWorkoutSession[]> {
    return this.userWorkoutSessions.filter((session) => userProfileId === session.userProfileId);
  }

  public add(userWorkoutSession: UserWorkoutSession): Promise<void> {
    return new Promise((resolve) => {
      this.userWorkoutSessions.push(userWorkoutSession);
      resolve();
    });
  }

  public addOrUpdate(userWorkoutSession: UserWorkoutSession): Promise<void> {
    return new Promise(async (resolve) => {
      const session = await this.getWorkoutSessionFromId(userWorkoutSession.uniqueReference);
      if (session) {
        await this.update(userWorkoutSession);
      } else {
        await this.add(userWorkoutSession);
      }
      resolve();
    });
  }

  public remove(userWorkoutSession: UserWorkoutSession): Promise<void> {
    return new Promise((resolve) => {
      this.userWorkoutSessions = this.userWorkoutSessions.filter((s) => s !== userWorkoutSession);
      resolve();
    });
  }

  public update(userWorkoutSession: UserWorkoutSession): Promise<void> {
    const sessionIndex = this.userWorkoutSessions
      .findIndex((session) => session.uniqueReference === userWorkoutSession.uniqueReference);

    if (sessionIndex < 0) {
      return Promise.reject(new Error('Session not found'));
    }

    this.userWorkoutSessions[sessionIndex] = userWorkoutSession;
    return Promise.resolve();
  }

  public async bulkUpdate(userWorkoutSessions: UserWorkoutSession[]): Promise<UserWorkoutSession[]> {
    await Promise.all(userWorkoutSessions.map((session) => this.update(session)));
    return [];
  }

  public updateWorkoutInstructorSetting(uniqueReference: string, instructorId: string): Promise<void> {
    const sessionIndex = this.userWorkoutSessions.findIndex((session) => session.uniqueReference === uniqueReference);

    if (sessionIndex < 0) {
      return Promise.reject(new Error('Session not found'));
    }

    this.userWorkoutSessions[sessionIndex].settings.homeWorkoutInstructor = instructorId;
    return Promise.resolve();
  }

  public updateWorkoutSettings(uniqueReference: string, workoutSettings: WorkoutSettings): Promise<void> {
    const sessionIndex = this.userWorkoutSessions.findIndex((session) => session.uniqueReference === uniqueReference);

    if (sessionIndex < 0) {
      return Promise.reject(new Error('Session not found'));
    }

    this.userWorkoutSessions[sessionIndex].settings = workoutSettings;
    return Promise.resolve();
  }

  public get(
    gym?: Gym,
    userId?: string,
    from: Date = new Date(),
    to?: Date,
    filter: (sessions: UserWorkoutSession[]) => UserWorkoutSession[] = ((s) => s),
  ): Promise<UserWorkoutSession[]> {
    return new Promise((resolve) => {
      const sessions = filter(this.filterSessionsForUser(this.getSessionsBetweenDate(from, to), userId));
      const gymSpecificSessions = sessions.filter((session) => (gym ? session.gymName === gym.name : true));
      resolve(gymSpecificSessions);
    });
  }

  public async getWorkoutHistory(userProfileId: string, after: Date): Promise<IUserWorkoutSessionSummary[]> {
    return this.userWorkoutSessions.filter((session) => session.userProfileId === userProfileId
        && !session.removedAt
        && session.endTime <= after);
  }

  public async getWorkoutsAfterPointInTime(userProfileId: string, from: Date): Promise<UserWorkoutSession[]> {
    return this.userWorkoutSessions.filter((session) => session.userProfileId === userProfileId
        && !session.removedAt
        && session.endTime >= from);
  }

  public async getLastWorkout(userProfileId: string, from: Date, to: Date): Promise<UserWorkoutSession | undefined> {
    return new Promise<UserWorkoutSession | undefined>((resolve) => {
      const userWorkoutSessions = this.userWorkoutSessions
        .filter((session) => session.userProfileId === userProfileId
            && !session.removedAt
            && session.endTime >= from
            && session.endTime <= to)
        .sort((a: any, b: any) => {
          if (a.endTime < b.endTime) {
            return -1;
          }
          if (a.endTime > b.endTime) {
            return 1;
          }
          return 0;
        });

      const lastWorkout = userWorkoutSessions[userWorkoutSessions.length - 1];
      resolve(lastWorkout);
    });
  }

  public async getWorkoutSessionToNotifyForReview(now: Date): Promise<UserWorkoutSession[]> {
    const sessions = this.userWorkoutSessions
      .filter((session) => session.checkedIn)
      .filter((session) => !session.removedAt)
      .filter((session) => !session.reviewNotified)
      .filter((session) => session.endTime < now);

    return Promise.resolve(sessions);
  }

  public getWorkoutSessionFromIdWithPairedSession(workoutId: string): Promise<UserWorkoutSession | undefined> {
    throw new Error('Method not implemented.');
  }

  public async getWorkoutsCurrentlyActiveInStudio(
    userProfileId: string,
    now: Date,
  ): Promise<UserWorkoutSession> {
    const homeGymNames = DbGymRepository.getGymNames(GymTypes.Home);
    return this.userWorkoutSessions.filter((session) => !session.removedAt
      && session.endTime >= now
      && session.startTimeOfFirstExercise <= now
      && session.userProfileId === userProfileId
      && !homeGymNames.includes(session.gymName))[0];
  }

  public async getConfirmedBookingsAndInvitesAndProvisional(
    userId?: string,
  ): Promise<UserWorkoutSession[]> {
    const workoutsFrom7DaysAgoTill7DaysAhead = this.userWorkoutSessions.filter((session) => !session.removedAt
        && session.endTime >= TimeSpan.getAWeekAgo(new Date())
        && session.endTime <= TimeSpan.getAWeekAhead(new Date()));
    return this.filterSessionsForUser(workoutsFrom7DaysAgoTill7DaysAhead, userId);
  }

  public async getHistoryBetweenDates(userProfileId: string, from: Date, to: Date):
  Promise<IUserWorkoutSessionSummary[]> {
    return this.userWorkoutSessions.filter((session) => session.userProfileId === userProfileId
        && session.endTime > from
        && session.endTime < to);
  }

  public getWorkoutSessionFromId(workoutId: string): Promise<UserWorkoutSession | undefined> {
    return new Promise((resolve) => {
      const sessions = this.userWorkoutSessions
        .filter((session: UserWorkoutSession) => session.uniqueReference === workoutId);
      resolve(sessions[0]);
    });
  }

  public getFromExternalReferences(references: IExternalReference): Promise<UserWorkoutSession | undefined> {
    throw new Error('Method not implemented.');
  }

  public getExpiredAndNotNotifiedAndNotRemovedProvisionalSessions(
    dateToCheckFor: Date,
    filter: (sessions: UserWorkoutSession[]) => UserWorkoutSession[] = ((s) => s),
  ): Promise<UserWorkoutSession[]> {
    return new Promise((resolve) => {
      const sessions = this.userWorkoutSessions
        .filter((session: UserWorkoutSession) => !session.removedAt && !session.expiryNotified
          && session.expiresAt && session.expiresAt.getTime() <= dateToCheckFor.getTime());
      resolve(
        this.sortSessions(filter(sessions)),
      );
    });
  }

  public async getNonCheckedInAndNotRemoveWorkoutsStartedBefore(
    time: Date,
    checkInNotified: boolean,
    filter: (sessions: UserWorkoutSession[]) => UserWorkoutSession[] = ((s) => s),
  ): Promise<UserWorkoutSession[]> {
    return new Promise<UserWorkoutSession[]>((resolve) => {
      resolve(
        filter(this.userWorkoutSessions
          .filter((session: UserWorkoutSession) => session.startTime.getTime() <= time.getTime()
            && !session.removedAt
            && !session.checkedIn)),
      );
    });
  }

  public async getWorkoutsWithFirstExerciseOn(now: Date): Promise<UserWorkoutSession[]> {
    return this.userWorkoutSessions.filter((session: UserWorkoutSession) => !session.removedAt
      && session.startTimeOfFirstExercise.getTime() === now.getTime());
  }

  public async getNextWorkout(userProfileId: string, after: Date): Promise<NextWorkout | undefined> {
    return undefined;
  }

  public getAllPairedWorkouts(workoutId: string, requestTime: Date): Promise<UserWorkoutSession[]> {
    return new Promise((resolve) => {
      const sessions = this.userWorkoutSessions
        .filter((session: UserWorkoutSession) => session.pairedSessionReference === workoutId
          || session.uniqueReference === workoutId);
      resolve(sessions);
    });
  }

  public async getWorkoutsForLeaderboard(from: Date): Promise<UserWorkoutSession[]> {
    throw new Error('Method not implemented.');
  }

  public getAllActiveInstructors(): Promise<any[]> {
    return Promise.resolve([]);
  }

  public getUserWorkoutCount(userProfileId: string): Promise<number> {
    return Promise.resolve(0);
  }

  public async getUserWorkoutCountForGym(
    uid: string,
    gymName: string,
  ): Promise<number> {
    throw new Error('Method not implemented.');
  }

  public getUserWorkoutAggregates(
    gymName: string,
    tz: string,
    workoutsDateRange?: { workoutsFromDate: Date, workoutsToDate?: Date },
  ): Promise<IUserWorkoutAggregate[]> {
    throw new Error('Method not implemented.');
  }

  public async getLastWorkoutWorkoutSelectionId(uid: string, settings: WorkoutSettings, gymType: GymTypes):
  Promise<{ startTime: Date; workoutSelectionIndex: number; workoutSelectionKey: string; } | undefined> {
    const workoutSessions = this.userWorkoutSessions
      .filter((session) => session.userProfileId === uid && session.settings === settings);
    const sortedSessions = this.sortSessions(workoutSessions);

    const lastWorkoutSession = sortedSessions[0];

    let lastWorkoutWorkoutSelectionId;

    if (lastWorkoutSession && lastWorkoutSession.workoutSelectionId) {
      const firstSeperatorIndex = lastWorkoutSession.workoutSelectionId.indexOf('_');
      const workoutSelectionIndex = lastWorkoutSession.workoutSelectionId.substring(0, firstSeperatorIndex);
      const workoutSelectionSettingsKey = lastWorkoutSession.workoutSelectionId.substring(
        firstSeperatorIndex + 1,
        lastWorkoutSession.workoutSelectionId.length,
      );

      lastWorkoutWorkoutSelectionId = {
        startTime: lastWorkoutSession.startTime,
        workoutSelectionIndex: +workoutSelectionIndex,
        workoutSelectionKey: workoutSelectionSettingsKey,
      };
    }

    return Promise.resolve(lastWorkoutWorkoutSelectionId);
  }

  public async getAllSessionsInDateRangeForGym(from: Date, to: Date, gym: Gym): Promise<UserWorkoutSession[]> {
    const filteredSessions = this.userWorkoutSessions.filter((userWorkoutSession: UserWorkoutSession) => {
      const isInPeriod = userWorkoutSession.startTime.getTime() <= to.getTime()
        && userWorkoutSession.endTime.getTime() >= from.getTime();
      const isInGym = gym ? userWorkoutSession.gymName === gym.name : true;

      return isInPeriod && isInGym;
    });

    return filteredSessions;
  }

  public async getForUserThatEndedInTimeRange(
    from: Date,
    to: Date,
    userProfileId: string,
  ): Promise<UserWorkoutSession[]> {
    const filtered = this.userWorkoutSessions.filter((userWorkoutSession) => {
      const shouldProcessWorkout = userWorkoutSession.startTime >= from && userWorkoutSession.startTime <= to;
      return shouldProcessWorkout;
    });
    return filtered;
  }

  public updateWorkoutSessionSettings(
    workoutId: string,
    uid: string,
    enabledFeatures: IEnabledFeatures,
    workoutSettings: WorkoutSettings,
  ): Promise<void> {
    const workoutSession = this.userWorkoutSessions
      .find((userWorkoutSession: UserWorkoutSession) => userWorkoutSession.uniqueReference === workoutId);
    if (!workoutSession) {
      throw new Error(`No workout session found for id ${workoutId}`);
    }

    const gym = DbGymRepository.getByName(workoutSession.gymName);
    const workoutSelectionId = this.userWorkoutSessions
      .find((session) => session.settings === workoutSettings && session.gymName === gym.name)?.workoutSelectionId;

    if (!workoutSelectionId) {
      throw new Error(`No workout selection found for settings ${JSON.stringify(workoutSettings)}`);
    }
    workoutSession.settings = workoutSettings;
    workoutSession.workoutSelectionId = workoutSelectionId;
    this.update(workoutSession).catch((e) => { /* no-op */ });
    return Promise.resolve();
  }

  public getWorkoutsForStudioLeaderboard(from: Date, to: Date, gymName: string): Promise<UserWorkoutSession[]> {
    throw new Error('Method not implemented.');
  }

  public getLeaderboardData(
    leaderboardInstance: ILeaderboardInstance,
    showIndividual: boolean = true,
    team?: string,
  ): Promise<LeaderboardData> {
    throw new Error('Method not implemented.');
  }

  public getLeaderboardsForUser(userId: string, fromDate: Date): Promise<IUserLeaderboardInstance[]> {
    throw new Error('Method not implemented.');
  }

  public getNotCompletedNotRemovedWorkouts(time: Date): Promise<UserWorkoutSession[]> {
    throw new Error('Method not implemented.');
  }

  private filterSessionsForUser(sessions: UserWorkoutSession[], userId?: string) {
    if (!userId) {
      return sessions;
    }
    return sessions.filter((session: UserWorkoutSession) => session.userProfileId === userId);
  }

  private getSessionsBetweenDate(from: Date, to?: Date) {
    return this.sortSessions(this.userWorkoutSessions.filter((session: UserWorkoutSession) => {
      let timeCheck = session.endTime.getTime() >= from.getTime();
      if (to) {
        timeCheck = timeCheck && session.startTime.getTime() <= to!.getTime();
      }
      return timeCheck;
    }));
  }

  private sortSessions(sessions: UserWorkoutSession[]): UserWorkoutSession[] {
    return sessions.sort((a: UserWorkoutSession, b: UserWorkoutSession) => {
      if (a.startTime.getTime() > b.startTime.getTime()) {
        return 1;
      }
      return -1;
    });
  }
}
